import * as _ from 'underscore';
import { PermissionsHasher } from '../helpers/PermissionsHasher';
import { Module } from '../Module';
import { CapabilitiesByContentType } from '../ServerApi/types/Capabilities';
import { Permission } from './Permission';
import { PermissionTemplateRoleDefinitions } from './PermissionTemplateRoleDefinitions';
import { Role } from './Role';

// These role names also serve as the roles' localization keys
export const SpecialRoleNames = {
  Custom: 'custom',
  Denied: 'denied',
  None: 'none'
};

interface IPermissionsAndRoles {
  permissionsByRole: _.Dictionary<_.Dictionary<Permission>>;
  explicitRolesByPermissionsHash: _.Dictionary<string>;
  effectiveRolesByPermissionsHash: _.Dictionary<string>;
}

export class PermissionRoles {

  private permissionsAndRolesByType: _.Dictionary<IPermissionsAndRoles>;
  private capabilitiesByContentType: _.Dictionary<string[]>;
  private permissionsHasher: PermissionsHasher;

  constructor(explicitRolesByContentType: _.Dictionary<Role[]>,
              capabilitiesByContentType: _.Dictionary<string[]>) {

    this.permissionsAndRolesByType = {};
    this.capabilitiesByContentType = capabilitiesByContentType;
    const allCapabilities = _.uniq(_.flatten(_.values(capabilitiesByContentType)));
    this.permissionsHasher = new PermissionsHasher(allCapabilities);

    _.each(explicitRolesByContentType, (roles: Role[], contentType: string) => {

      _.each(roles, (role: Role) => {
        this.addRoleForContentType(contentType, role);
      });

      // Add the 'unspecified' and 'denied' roles
      const unspecifiedRole: _.Dictionary<Permission> = {};
      const deniedRole: _.Dictionary<Permission> = {};
      _.each(capabilitiesByContentType[contentType], (capability: string) => {
        unspecifiedRole[capability] = Permission.Unspecified;
        deniedRole[capability] = Permission.Denied;
      });
      this.addExplicitRole(contentType, unspecifiedRole, SpecialRoleNames.None);
      this.addExplicitRole(contentType, deniedRole, SpecialRoleNames.Denied);
      this.addEffectiveRole(contentType, deniedRole, SpecialRoleNames.None);
    });
  }

  /*
   * Gets the single explicit role that describes this set of permissions on an authorizable type.
   * Returns 'custom' if there is no applicable role.
   */
  public getExplicitRoleForTypeAndPermissions(contentType: string, permissions: _.Dictionary<Permission>): string {
    return this.getRoleForPermissions(this.getExplicitRolesForType(contentType), permissions);
  }

  /*
   * Gets the single effective role that describes this set of permissions on an authorizable type.
   * Returns 'custom' if there is no applicable role.
   */
  public getEffectiveRoleForTypeAndPermissions(contentType: string, permissions: _.Dictionary<Permission>): string {
    return this.getRoleForPermissions(this.getEffectiveRolesForType(contentType), permissions);
  }

  /*
   * Gets the set of explicit permissions for this role.
   */
  public getPermissionsForTypeAndRole(contentType: string, role: string): _.Dictionary<Permission> {
    return this.getPermissionsByRoleForType(contentType)[role];
  }

  /*
   * Gets the list of roles for this authorizable type
   */
  public getAvailableRolesForType(contentType: string): string[] {
    return _.values(this.getExplicitRolesForType(contentType));
  }


  private getPermissionsAndRolesForType(type: string): IPermissionsAndRoles {
    let permissionsAndRoles = this.permissionsAndRolesByType[type];
    if (!permissionsAndRoles) {
      permissionsAndRoles = {
        permissionsByRole: {},
        explicitRolesByPermissionsHash: {},
        effectiveRolesByPermissionsHash: {}
      };
      this.permissionsAndRolesByType[type] = permissionsAndRoles;
    }
    return permissionsAndRoles;
  }

  private getPermissionsByRoleForType(type: string) {
    return this.getPermissionsAndRolesForType(type).permissionsByRole;
  }

  private getExplicitRolesForType(type: string) {
    return this.getPermissionsAndRolesForType(type).explicitRolesByPermissionsHash;
  }

  private getEffectiveRolesForType(type: string) {
    return this.getPermissionsAndRolesForType(type).effectiveRolesByPermissionsHash;
  }

  private getRoleForPermissions(roles: _.Dictionary<string>, permissions: _.Dictionary<Permission>): string {
    return roles[this.permissionsHasher.hash(permissions)] || SpecialRoleNames.Custom;
  }

  private setRoleForPermissions(roles: _.Dictionary<string>, permissions: _.Dictionary<Permission>, role: string) {
    roles[this.permissionsHasher.hash(permissions)] = role;
  }

  private addRoleForContentType(type: string, role: Role) {
    const allCapabilities = this.capabilitiesByContentType[type];
    const explicitPermissions: _.Dictionary<Permission> = {};
    const effectivePermissions: _.Dictionary<Permission> = {};

    _.each(allCapabilities, (capability: string) => {
      const permission = role.permissions[capability] || Permission.Unspecified;
      explicitPermissions[capability] = permission;
      // Unspecified permissions are effectively denied
      effectivePermissions[capability] =
        (permission === Permission.Unspecified) ? Permission.Denied : permission;
    });

    this.addExplicitRole(type, explicitPermissions, role.locKey);
    this.addEffectiveRole(type, effectivePermissions, role.locKey);
  }

  private addExplicitRole(type: string, permissions: _.Dictionary<Permission>, role: string) {
    const permissionRoles = this.getPermissionsAndRolesForType(type);
    permissionRoles.permissionsByRole[role] = permissions;
    this.setRoleForPermissions(permissionRoles.explicitRolesByPermissionsHash, permissions, role);
  }

  private addEffectiveRole(type: string, permissions: _.Dictionary<Permission>, role: string) {
    // NOTE: effective permissions aren't stored in the permissionsByRole map because this map
    // is only used when setting explicit permissions for a grantee.
    const permissionRoles = this.getPermissionsAndRolesForType(type);
    this.setRoleForPermissions(permissionRoles.effectiveRolesByPermissionsHash, permissions, role);
  }

}

Module.instance().factory('PermissionRoles', () => {
  return new PermissionRoles(PermissionTemplateRoleDefinitions.explicitRolesByContentType,
      CapabilitiesByContentType);
});
